import React, { useState, useCallback, useMemo } from "react";
import { Form, Drawer, Button, Modal } from "antd";
import { connect } from "dva";
import { ShipmentStatus, QuoteStatus } from "@/utils/enum";
import { uploadGatePass } from "@/utils/gatepass";
import { showJiraWidget, getUrlParams, parseAsNumber, parseVehicleData } from "@/utils/utils";
import useScreenDetector from "@/hooks/useScreenDetector";
import DrawerWrapper from "@/components/DrawerWrapper";
import VehicleForm from "@/forms/shared/Vehicle/VehicleForm";

const { confirm } = Modal;

const EditVehicleDrawer = ({ vehicleData, dealerId, shipmentStatus, allVehicles, configPerm, dispatch }) => {
  const [editVehicleFormRef] = Form.useForm();

  const { isMobile } = useScreenDetector(768);

  const [loading, setLoading] = useState(false);

  const getVehiclePayload = useCallback(
    data => ({
      dealerId,
      vin: data.vin?.toUpperCase(),
      stockNumber: data.stockNumber,
      year: data.year,
      make: data.make,
      model: data.model,
      color: data.customColor ?? data.color,
      vehicleType: data.vehicleType,
      drivabilityType: data.inoperational,
      otherType: data.vehicleTypeComment ?? "",
      comments: data.comments,
      curbWeight: data.curbWeight,
      trimPackage: data.trimPackageName,
      bodySubType: data.bodySubType,
      exteriorDimensions: data.exteriorDimensions,
      // exterior dimensions
      width: parseAsNumber(data.width, 0),
      length: parseAsNumber(data.length, 0),
      height: parseAsNumber(data.height, 0),
      truckWithLongWheelBase: data.truckWithLongWheelBase ?? false,
      truckWithDualWheels: data.truckWithDualWheels ?? false,
      isEV: data.isEV,
    }),
    [dealerId],
  );

  const sendRegulatedFormData = useCallback(
    async (data, gatePassPayload) => {
      const shipmentId = getUrlParams("shipmentId");

      const vehiclePayload = getVehiclePayload(data);

      const payload = {
        shipmentId,
        vehicleId: vehicleData.vehicleId,
        ...vehiclePayload,
        gatePassType: data.gatePassStatus,
        gatePassUrl: gatePassPayload,
      };

      await dispatch({
        type: "shipmentDetails/editRegulatedVehicle",
        shipmentId,
        vehicleId: vehicleData.id,
        payload,
      }).then(() => {
        dispatch({
          type: "shipmentDetails/setEditVehicleData",
          payload: {},
        });

        setLoading(false);
        showJiraWidget();
        editVehicleFormRef.resetFields();
      });
    },
    [vehicleData, dispatch, getVehiclePayload, editVehicleFormRef],
  );

  const sendFormData = useCallback(
    async (data, gatePassPayload) => {
      const shipmentId = getUrlParams("shipmentId");

      const vehiclePayload = getVehiclePayload(data);
      const payload = {
        shipmentId,
        vehicleId: vehicleData.vehicleId,
        ...vehiclePayload,
        gatePassType: data.gatePassStatus,
        gatePassUrl: gatePassPayload,
      };

      await dispatch({
        type: "shipmentDetails/editVehicle",
        shipmentId,
        vehicleId: vehicleData.id,
        payload,
      }).then(() => {
        dispatch({
          type: "shipmentDetails/setEditVehicleData",
          payload: {},
        });

        setLoading(false);
        showJiraWidget();
        editVehicleFormRef.resetFields();
      });
    },
    [vehicleData, dispatch, getVehiclePayload, editVehicleFormRef],
  );

  const checkForRestrictedFieldModified = useCallback(
    result => {
      const fields = {
        trimPackageName: "trimPackage",
        year: "year",
        make: "make",
        model: "model",
        curbWeight: "curbWeight",
        bodySubType: "bodySubType",
        exteriorDimensions: "exteriorDimensions",
        width: "width", // exterior dimensions
        length: "length", // exterior dimensions
        height: "height", // exterior dimensions
        vehicleType: "vehicleType",
        truckWithLongWheelBase: "truckWithLongWheelBase",
        truckWithDualWheels: "truckWithDualWheels",
        // vehicleTypeComment,
        inoperational: "drivabilityType",
      };

      let restrictedFieldModified = false;

      if (shipmentStatus >= ShipmentStatus.Pending && shipmentStatus <= ShipmentStatus.Completed) {
        const entries = Object.entries(fields);

        for (let i = 0; i < entries.length && !restrictedFieldModified; i++) {
          const [fieldKey, vehicleKey] = entries[i];
          const formValue = result[fieldKey];
          const vehicleValue = vehicleData[vehicleKey];
          const formValueNonNullish = formValue !== null && formValue !== undefined;
          const vehicleValueNonNullish = vehicleValue !== null && vehicleValue !== undefined;

          restrictedFieldModified = formValue !== vehicleValue && (formValueNonNullish || vehicleValueNonNullish);
        }
      }

      return restrictedFieldModified;
    },
    [shipmentStatus, vehicleData],
  );

  const onSubmit = useCallback(
    async result => {
      let gatePassPayload = result.gatePassUrl;

      setLoading(true);

      // if we have a file to upload
      if (result?.gatePassFile) {
        gatePassPayload = await uploadGatePass(result.gatePassFile);
      }

      const restrictedFieldModified = checkForRestrictedFieldModified(result);

      const hasOneCompletedQuote = allVehicles.some(vehicle => vehicle.quote.status === QuoteStatus.Completed);

      if (restrictedFieldModified && hasOneCompletedQuote) {
        confirm({
          title: "Restricted field modified",
          content: "Modifying this vehicle will remove all other vehicles quotes.",
          onOk: async () => {
            await sendFormData(result, gatePassPayload);
          },
        });
      } else if (shipmentStatus >= ShipmentStatus.Posted) {
        await sendRegulatedFormData(result, gatePassPayload);
      } else if (
        !restrictedFieldModified &&
        hasOneCompletedQuote &&
        [ShipmentStatus.Pending, ShipmentStatus.Completed].includes(shipmentStatus)
      ) {
        await sendRegulatedFormData(result, gatePassPayload);
      } else {
        await sendFormData(result, gatePassPayload);
      }
    },
    [allVehicles, sendFormData, sendRegulatedFormData, checkForRestrictedFieldModified, shipmentStatus],
  );

  const vehicleFormData = useMemo(() => parseVehicleData(vehicleData), [vehicleData]);

  return (
    <DrawerWrapper visible={vehicleData.id !== undefined}>
      <Drawer
        title="Edit vehicle"
        width={isMobile ? "80%" : "40%"}
        onClose={() => {
          dispatch({
            type: "shipmentDetails/setEditVehicleData",
            payload: {},
          });

          showJiraWidget();
        }}
        visible={vehicleData.id !== undefined}
        bodyStyle={{ paddingBottom: 80 }}
        footer={
          <div style={{ textAlign: "right" }}>
            <Button
              onClick={() => {
                dispatch({
                  type: "shipmentDetails/setEditVehicleData",
                  payload: {},
                });
                showJiraWidget();
                editVehicleFormRef?.resetFields();
              }}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading} onClick={editVehicleFormRef.submit}>
              Save
            </Button>
          </div>
        }
      >
        {vehicleData.id !== undefined && (
          <VehicleForm
            formRef={editVehicleFormRef}
            shipmentStatus={shipmentStatus}
            configPerm={configPerm}
            defaultValues={vehicleFormData}
            onSubmit={onSubmit}
            editMode
          />
        )}
      </Drawer>
    </DrawerWrapper>
  );
};

export default connect(({ shipmentDetails, configs }) => ({
  vehicleData: shipmentDetails.editVehicleData,
  allVehicles: shipmentDetails.data?.vehicles || [],
  dealerId: shipmentDetails.data?.dealer?.id,
  isInternalShipment: shipmentDetails.data.isInternalShipment,
  shipmentStatus: shipmentDetails.data?.status,
  configPerm: configs.configPerm,
}))(EditVehicleDrawer);
